/* Il risultato operativo: grafici e operazioni, per un backtest o per un bot.
 *
 * Un componente solo, tre punti di innesto: il dettaglio di un backtest
 * salvato, il backtest libero appena lanciato e — è la ragione per cui sta qui
 * in `componenti/` e non fra le pagine dei backtest — il dettaglio di un
 * tradebot. Lì serve a rispondere alla domanda che oggi non ha risposta: il bot
 * sta vendendo dove vendeva in prova? Un bot che opera diversamente da come
 * aveva operato nel backtest è la cosa più importante da notare, e per notarla
 * i due elenchi devono essere disegnati dallo stesso codice, con le stesse
 * convenzioni di colore e la stessa scala.
 *
 * L'ingresso è normalizzato (`lib/operazioni.ts`): dentro non si sa più se i
 * dati vengano da un backtest o da un bot, tranne dove cambia ciò che si può
 * DIRE — un bot non ha una curva del capitale, e va detto, non simulato.
 *
 * Due casi che non sono eccezioni:
 *  - ZERO operazioni con le serie presenti è un risultato, non un dato
 *    mancante: la strategia non è mai entrata;
 *  - UNA operazione, per giunta aperta, è lo stato normale di un tradebot
 *    (quello in produzione ne ha esattamente una). La schermata non deve
 *    sembrare rotta perché la lista ha una riga sola.
 */

import { Suspense, lazy, useMemo, useState, type ReactNode } from 'react';
import { importoDinamico, importoRiuscito } from '../lib/importoDinamico';
import { Avvertenza, Pulsante } from './Base';
import { GraficoEquity } from './Grafici';
import { Paginazione } from './Ordinamento';
import { dataOra, drawdown as fmtDrawdown, num, pct } from '../lib/formato';
import {
  riepilogoOperazioni,
  type OperazioneNormalizzata,
  type SerieRisultato,
} from '../lib/operazioni';

/* `lightweight-charts` pesa un paio di centinaia di kilobyte: caricarla
   all'avvio la farebbe pagare anche a chi apre solo la dashboard. Qui arriva
   quando un risultato con le serie viene davvero aperto — sul telefono è la
   differenza fra una schermata che parte e una che aspetta. Per lo stesso
   motivo `GraficoOperazioni` NON è esportato da `componenti/index.ts`. */
/* L'import passa da `importoDinamico`: dopo un rilascio il file col nome vecchio non esiste
   piu', e senza il recupero chi ha la pagina aperta vede morire l'applicazione con «Failed to
   fetch dynamically imported module» appena apre un risultato. E' accaduto in produzione. */
const GraficoOperazioni = lazy(() =>
  importoDinamico(
    () => import('./GraficoOperazioni'),
    'GraficoOperazioni',
  ).then((m) => {
    importoRiuscito();
    return { default: m.GraficoOperazioni };
  }),
);

/** Punti disegnabili nella curva del capitale senza far scattare il thread. */
const PUNTI_EQUITY = 720;

/** Operazioni per pagina: la lista arriva tutta, la si stampa a pezzi. */
const OPERAZIONI_PER_PAGINA = 25;

function riduci<T>(serie: T[], quanti: number): T[] {
  if (serie.length <= quanti) return serie;
  const passo = serie.length / quanti;
  const fuori: T[] = [];
  for (let i = 0; i < quanti; i++) fuori.push(serie[Math.floor(i * passo)]);
  fuori.push(serie[serie.length - 1]);
  return fuori;
}

export interface PropsRisultatoOperazioni {
  serie: SerieRisultato;
  /**
   * Cosa mostrare quando non c'è NIENTE da mostrare: né operazioni né serie.
   * Lo decide chi monta il componente, perché la causa cambia — per un
   * backtest di griglia le serie sono state scartate e si può rieseguire, per
   * un bot appena avviato semplicemente non è ancora successo niente.
   */
  quandoVuoto?: ReactNode;
}

export function RisultatoOperazioni({ serie, quandoVuoto }: PropsRisultatoOperazioni) {
  const equity = useMemo(() => riduci(serie.equity ?? [], PUNTI_EQUITY), [serie.equity]);
  const { operazioni, prezzi, periodo } = serie;

  if (operazioni.length === 0 && !prezzi && !serie.equity) {
    return <>{quandoVuoto ?? <NienteDaMostrare serie={serie} />}</>;
  }

  return (
    <>
      {prezzi && prezzi.length > 1 ? (
        <div className="sb-bt-riquadro">
          <div className="sb-bt-riquadro__testata">
            <span className="sb-bt-riquadro__titolo">
              Prezzo, operazioni{serie.equity ? ' e drawdown' : ''}
            </span>
            <span className="sb-nota">
              {serie.coppia} · {periodo}
            </span>
          </div>
          <Suspense
            fallback={
              <div className="sb-scheletro" style={{ height: 300, borderRadius: 'var(--sb-r-3)' }} />
            }
          >
            <GraficoOperazioni
              prezzi={prezzi}
              operazioni={operazioni}
              equity={serie.equity}
              coppia={serie.coppia}
            />
          </Suspense>
        </div>
      ) : (
        <div className="sb-bt-riquadro">
          <div className="sb-bt-riquadro__testata">
            <span className="sb-bt-riquadro__titolo">Prezzo e operazioni</span>
          </div>
          {/* Senza la serie dei prezzi il grafico non è disegnabile, e non si
              inventa: le operazioni restano leggibili nell'elenco. */}
          <Avvertenza livello="nota">
            {serie.origine === 'tradebot'
              ? "Un tradebot non registra la serie dei prezzi: senza, le operazioni non si possono sovrapporre a un grafico. L'elenco qui sotto è completo."
              : 'Questa riga non porta la serie dei prezzi, quindi il grafico non è disegnabile. L’elenco delle operazioni resta completo.'}
          </Avvertenza>
        </div>
      )}

      {equity.length > 1 && (
        <div className="sb-bt-riquadro">
          <div className="sb-bt-riquadro__testata">
            <span className="sb-bt-riquadro__titolo">Capitale nel tempo</span>
            <span className="sb-numero sb-tono-perdita" style={{ fontSize: 'var(--sb-t-11)' }}>
              massimo drawdown{' '}
              {serie.drawdownMax == null ? 'n/d' : fmtDrawdown(serie.drawdownMax)}
            </span>
          </div>
          <div className="sb-nota" style={{ marginBottom: 'var(--sb-s-5)' }}>
            scala logaritmica · {periodo} · {num(equity.length)} punti su{' '}
            {num(serie.equity?.length ?? 0)}
          </div>
          <GraficoEquity punti={equity} />
        </div>
      )}

      <AnalisiOperazioni operazioni={operazioni} serie={serie} />

      {operazioni.length > 0 ? (
        <ElencoOperazioni operazioni={operazioni} />
      ) : (
        <div className="sb-bt-riquadro">
          <div className="sb-bt-riquadro__testata">
            <span className="sb-bt-riquadro__titolo">Operazioni</span>
          </div>
          {/* Zero operazioni CON le serie presenti è un risultato, non
              un'assenza: chiamarlo «nessun dato» farebbe cercare un guasto. */}
          <Avvertenza livello="nota">
            {serie.origine === 'tradebot'
              ? `Il bot non ha ancora aperto nessuna operazione su ${periodo}.`
              : `Nessuna operazione: su ${periodo} questa configurazione non è mai entrata a mercato. Il capitale è rimasto fermo, e il confronto con il mercato misura solo quello.`}
          </Avvertenza>
        </div>
      )}
    </>
  );
}

function NienteDaMostrare({ serie }: { serie: SerieRisultato }) {
  return (
    <div className="sb-bt-riquadro">
      <div className="sb-bt-riquadro__testata">
        <span className="sb-bt-riquadro__titolo">Operazioni</span>
      </div>
      <Avvertenza livello="nota">
        {serie.operazioniDichiarate
          ? `Sono dichiarate ${num(serie.operazioniDichiarate)} operazioni ma l'elenco non è stato salvato: non c'è niente da disegnare.`
          : serie.origine === 'tradebot'
            ? `Questo bot non ha ancora aperto nessuna operazione su ${serie.periodo}: finché non entra a mercato non c'è niente da disegnare, e non è un guasto.`
            : 'Non ci sono operazioni né serie da mostrare su questo periodo.'}
      </Avvertenza>
    </div>
  );
}

/* ==========================================================================
 * DA DOVE VIENE IL RISULTATO
 * ======================================================================== */

function AnalisiOperazioni({
  operazioni,
  serie,
}: {
  operazioni: OperazioneNormalizzata[];
  serie: SerieRisultato;
}) {
  const r = useMemo(() => riepilogoOperazioni(operazioni), [operazioni]);
  if (operazioni.length === 0) return null;

  /* Con una sola operazione chiusa, «migliore» e «peggiore» sarebbero la stessa
     riga stampata due volte: si mostra una scheda sola e si dice che è l'unica.
     È il caso normale di un tradebot, non un'eccezione da gestire male. */
  const unaSola = r.chiuse === 1;

  return (
    <div className="sb-bt-riquadro">
      <div className="sb-bt-riquadro__testata">
        <span className="sb-bt-riquadro__titolo">Da dove viene il risultato</span>
        <span className="sb-nota">{serie.periodo}</span>
      </div>

      <div className="sb-griglia">
        {r.chiuse === 0 ? (
          <div className="sb-bt-riquadro-soglia">
            <div className="sb-bt-riquadro-soglia__testata">
              <span className="sb-bt-riquadro-soglia__nome">NESSUNA CHIUSA</span>
            </div>
            <div className="sb-nota">
              {num(r.aperte)} {r.aperte === 1 ? 'operazione ancora aperta' : 'operazioni ancora aperte'}: finché
              non si chiude non ha un esito, e un esito non c'è — non è zero.
            </div>
          </div>
        ) : unaSola ? (
          <SchedaOperazioneNotevole titolo="Unica operazione chiusa" operazione={r.migliore} />
        ) : (
          <>
            <SchedaOperazioneNotevole titolo="Operazione migliore" operazione={r.migliore} />
            <SchedaOperazioneNotevole titolo="Operazione peggiore" operazione={r.peggiore} />
          </>
        )}

        <div className="sb-bt-riquadro-soglia">
          <div className="sb-bt-riquadro-soglia__testata">
            <span className="sb-bt-riquadro-soglia__nome">RIEPILOGO</span>
          </div>
          <div className="sb-bt-parametri" style={{ marginTop: 'var(--sb-s-4)' }}>
            <Voce chiave="operazioni" valore={num(r.totali)} />
            {r.aperte > 0 && <Voce chiave="ancora aperte" valore={num(r.aperte)} />}
            <Voce chiave="in guadagno" valore={num(r.vinte)} tono="guadagno" />
            <Voce chiave="in perdita" valore={num(r.perse)} tono="perdita" />
            <Voce
              chiave="quota vincenti"
              valore={r.percentualeVinte == null ? 'n/d' : `${num(r.percentualeVinte, 1)}%`}
            />
            <Voce
              chiave="media per operazione"
              valore={r.media == null ? 'n/d' : pct(r.media, 2)}
              tono={r.media == null ? undefined : r.media >= 0 ? 'guadagno' : 'perdita'}
            />
          </div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
            Quota vincenti e media per operazione vanno lette insieme: nove vittorie e una perdita
            più grande della somma delle nove sono una strategia in perdita.
          </div>
        </div>
      </div>
    </div>
  );
}

function Voce({
  chiave,
  valore,
  tono,
}: {
  chiave: string;
  valore: string;
  tono?: 'guadagno' | 'perdita';
}) {
  return (
    <div className="sb-bt-parametro">
      <div className="sb-bt-parametro__chiave">{chiave}</div>
      <div className={`sb-bt-parametro__valore${tono ? ` sb-tono-${tono}` : ''}`}>{valore}</div>
    </div>
  );
}

function SchedaOperazioneNotevole({
  titolo,
  operazione,
}: {
  titolo: string;
  operazione: OperazioneNormalizzata | null;
}) {
  if (!operazione || operazione.esito == null) {
    return (
      <div className="sb-bt-riquadro-soglia">
        <div className="sb-bt-riquadro-soglia__testata">
          <span className="sb-bt-riquadro-soglia__nome">{titolo.toUpperCase()}</span>
        </div>
        <div className="sb-nota">Nessuna operazione con un esito registrato.</div>
      </div>
    );
  }

  const tono = operazione.esito >= 0 ? 'guadagno' : 'perdita';
  return (
    <div className="sb-bt-riquadro-soglia">
      <div className="sb-bt-riquadro-soglia__testata">
        <span className="sb-bt-riquadro-soglia__nome">{titolo.toUpperCase()}</span>
        <span className={`sb-bt-riquadro-soglia__valore sb-tono-${tono}`}>
          {pct(operazione.esito, 2)}
        </span>
      </div>
      <div className="sb-bt-parametri" style={{ marginTop: 'var(--sb-s-4)' }}>
        <Voce chiave="entrata" valore={dataOra(operazione.entrataIl)} />
        <Voce chiave="uscita" valore={operazione.uscitaIl ? dataOra(operazione.uscitaIl) : 'aperta'} />
        <Voce
          chiave="prezzi"
          valore={`${prezzo(operazione.prezzoEntrata)} → ${prezzo(operazione.prezzoUscita)}`}
        />
      </div>
    </div>
  );
}

function prezzo(v: number | null): string {
  return v == null ? '—' : num(v, 5);
}

/* ==========================================================================
 * ELENCO PAGINATO
 * I dati arrivano tutti insieme: la paginazione è solo di resa. Trecento righe
 * stampate in una volta sono trecento righe che nessuno guarda, e su telefono
 * un elenco che non finisce più.
 * ======================================================================== */

function ElencoOperazioni({ operazioni }: { operazioni: OperazioneNormalizzata[] }) {
  const [pagina, setPagina] = useState(1);
  const pagineTotali = Math.max(1, Math.ceil(operazioni.length / OPERAZIONI_PER_PAGINA));
  const dentro = Math.min(pagina, pagineTotali);
  const da = (dentro - 1) * OPERAZIONI_PER_PAGINA;
  const viste = operazioni.slice(da, da + OPERAZIONI_PER_PAGINA);

  return (
    <div className="sb-bt-riquadro" style={{ padding: 0 }}>
      <div
        className="sb-bt-riquadro__testata"
        style={{ padding: 'var(--sb-s-8) var(--sb-s-8) var(--sb-s-5)', marginBottom: 0 }}
      >
        <span className="sb-bt-riquadro__titolo">Operazioni</span>
        <span className="sb-nota">
          {operazioni.length <= OPERAZIONI_PER_PAGINA
            ? `${num(operazioni.length)} in tutto`
            : `${num(da + 1)}–${num(da + viste.length)} di ${num(operazioni.length)}`}
        </span>
      </div>

      {/* Telefono: schede. Tablet in su: tabella. Entrambe renderizzate sempre,
          sceglie il CSS: il primo fotogramma è già giusto e la stampa funziona. */}
      <div className="sb-bt-op-schede">
        {viste.map((o, i) => (
          <SchedaOperazione key={`${o.entrataIl}-${da + i}`} operazione={o} numero={da + i + 1} />
        ))}
      </div>

      <div className="sb-bt-op-tabella">
        <div className="sb-scorri-x">
          <table className="sb-bt-operazioni">
            <thead>
              <tr>
                <th>#</th>
                <th>Lato</th>
                <th>Entrata</th>
                <th>Uscita</th>
                <th>Prezzo in</th>
                <th>Prezzo out</th>
                <th>Risultato</th>
              </tr>
            </thead>
            <tbody>
              {viste.map((o, i) => (
                <RigaOperazione key={`${o.entrataIl}-${da + i}`} operazione={o} numero={da + i + 1} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagineTotali > 1 && (
        <Paginazione
          pagina={dentro}
          pagineTotali={pagineTotali}
          perPagina={OPERAZIONI_PER_PAGINA}
          totale={operazioni.length}
          onVai={setPagina}
        />
      )}
    </div>
  );
}

function tonoDi(o: OperazioneNormalizzata): 'guadagno' | 'perdita' | 'neutro' {
  if (o.esito == null) return 'neutro';
  return o.esito >= 0 ? 'guadagno' : 'perdita';
}

function testoEsito(o: OperazioneNormalizzata): string {
  /* «aperta» e non «n/d»: la differenza fra un dato che manca e un'operazione
     che non è ancora finita è tutta la differenza. */
  if (o.esito == null) return o.uscitaIl ? 'n/d' : 'aperta';
  return pct(o.esito, 2);
}

function RigaOperazione({
  operazione: o,
  numero,
}: {
  operazione: OperazioneNormalizzata;
  numero: number;
}) {
  return (
    <tr>
      <td className="sb-tono-neutro">{numero}</td>
      <td className="sb-tono-neutro">LONG</td>
      <td className="sb-tono-neutro">{dataOra(o.entrataIl)}</td>
      <td className="sb-tono-neutro">{o.uscitaIl ? dataOra(o.uscitaIl) : 'aperta'}</td>
      <td className="sb-tono-neutro">{prezzo(o.prezzoEntrata)}</td>
      <td className="sb-tono-neutro">{prezzo(o.prezzoUscita)}</td>
      <td className={`sb-tono-${tonoDi(o)}`} style={{ fontWeight: 'var(--sb-peso-forte)' }}>
        {testoEsito(o)}
      </td>
    </tr>
  );
}

function SchedaOperazione({
  operazione: o,
  numero,
}: {
  operazione: OperazioneNormalizzata;
  numero: number;
}) {
  return (
    <div className="sb-bt-op-scheda">
      <div className="sb-bt-op-scheda__testata">
        <span className="sb-bt-op-scheda__numero">#{numero} · LONG</span>
        <span className={`sb-bt-op-scheda__esito sb-tono-${tonoDi(o)}`}>{testoEsito(o)}</span>
      </div>
      <div className="sb-bt-op-scheda__riga">
        <span className="sb-bt-op-scheda__chiave">entrata</span>
        <span className="sb-bt-op-scheda__valore">
          {dataOra(o.entrataIl)} · {prezzo(o.prezzoEntrata)}
        </span>
      </div>
      <div className="sb-bt-op-scheda__riga">
        <span className="sb-bt-op-scheda__chiave">uscita</span>
        <span className="sb-bt-op-scheda__valore">
          {o.uscitaIl ? dataOra(o.uscitaIl) : 'aperta'} · {prezzo(o.prezzoUscita)}
        </span>
      </div>
      {o.nota && <div className="sb-nota">{o.nota}</div>}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Un'azione facoltativa sotto il risultato: serve a chi monta il componente
 * per offrire «esegui», «apri il dettaglio» e simili senza doverne rifare la
 * cornice.
 * ------------------------------------------------------------------------ */

export function AzioneSulRisultato({
  etichetta,
  nota,
  onAzione,
  inCorso = false,
}: {
  etichetta: string;
  nota?: ReactNode;
  onAzione: () => void;
  inCorso?: boolean;
}) {
  return (
    <div className="sb-bt-riquadro">
      {nota && <div className="sb-bt-serie-assenti__spiega">{nota}</div>}
      <Pulsante variante="primario" onClick={onAzione} disabled={inCorso}>
        {inCorso ? 'In corso…' : etichetta}
      </Pulsante>
    </div>
  );
}
